import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { PhoneNav } from "../../../../shared/phone/PhoneNav";
import { PnRow } from "../../../../shared/phone/Row";
import { Icon } from "../../../components/ui/Icon";
import type { Dates } from "../../../lib/ccApi";
import { shortGoal } from "../text";
import { useHome } from "../useCc";
import { HomeActions, HomeSheets, Summary, type HomeSheet } from "./HomeNative";
import { useNumbers } from "./numbers";
import { Count, GoalRow, useHmToast, WaitRow } from "./parts";
import { inGoalOrder, waitingRows } from "./waiting";
import { useCan } from "../../../context/AuthContext";

const enc = encodeURIComponent;

/**
 * One product's part of Home (`/command/:productId`) on a phone and an iPad: its sentence, what needs the
 * owner on it, its goals, and its own pages.
 */
export function ProductNative() {
  const { productId = "" } = useParams();
  const { home, error } = useHome();
  const [dates, setDates] = useState<Dates>({ preset: "last_7d" });
  const nums = useNumbers(dates);
  const [sheet, setSheet] = useState<HomeSheet>(null);
  const [finished, setFinished] = useState(false);
  const { toast, say } = useHmToast();
  const p = home?.products.find((x) => x.id === productId) ?? null;
  const goals = p?.goals ?? [];
  const can = useCan();
  const rows = useMemo(() => (home ? inGoalOrder(waitingRows(home, can, productId)) : []), [home, can, productId]);
  const live = goals.filter((g) => g.state !== "done");
  const done = goals.filter((g) => g.state === "done");

  return (
    <div className="pn-screen hm-home">
      <PhoneNav large title={p?.name ?? "Product"} back={{ to: "/command", label: "Home" }} actions={p ? <HomeActions open={setSheet} /> : null} />
      <div className="pn-stack">
        {!home ? (
          <p className="pn-note">{error ?? "Loading the product…"}</p>
        ) : !p ? (
          <p className="pn-note">This product is not in your account.</p>
        ) : (
          <>
            <Summary goals={goals} nums={nums} dates={dates} onDates={() => setSheet("range")} who={p.name} loaded />
            {rows.length > 0 && (
              <section>
                <div className="pn-sh">
                  <h2>
                    Needs you <Count n={rows.length} />
                  </h2>
                  <Link className="pn-lnk" to="/needs">
                    See all
                  </Link>
                </div>
                <div className="pn-grp">
                  {rows.slice(0, 3).map((w) => (
                    <WaitRow key={w.key} w={w} to={`/needs/${enc(w.key)}`} sub={shortGoal(w.goal.sentence)} />
                  ))}
                </div>
              </section>
            )}
            <section>
              <div className="pn-sh">
                <h2>Goals</h2>
              </div>
              <div className="pn-grp">
                {live.map((g) => (
                  <GoalRow key={g.id} g={g} n={nums.get(g.id)} />
                ))}
                {!live.length && can("editor") && <PnRow to={`/products/${enc(p.id)}/goals/new`} className="accent" icon={<Icon name="plus" />} title="New goal" />}
              </div>
            </section>
            {done.length > 0 && (
              <section>
                <div className="pn-grp">
                  <PnRow
                    onClick={() => setFinished((f) => !f)}
                    title="Finished goals"
                    value={
                      <span className="hm-foldv">
                        {done.length}
                        <Icon name="chev" />
                      </span>
                    }
                    chevron={false}
                    className={finished ? "hm-fold open" : "hm-fold"}
                  />
                  {finished && done.map((g) => <GoalRow key={g.id} g={g} n={nums.get(g.id)} />)}
                </div>
              </section>
            )}
            <section>
              <div className="pn-grp">
                <PnRow to={`/products/${enc(p.id)}`} icon={<Icon name="doc" />} iconTone="plain" title="Product page" />
                {can("admin") && <PnRow to={`/products/${enc(p.id)}/partners`} icon={<Icon name="users" />} iconTone="plain" title="Partners" sub={`People who refer ${p.name} for a share`} />}
              </div>
            </section>
          </>
        )}
      </div>
      <HomeSheets
        sheet={sheet}
        close={() => setSheet(null)}
        products={p ? [p] : []}
        dates={dates}
        setDates={setDates}
        newTo={{ to: `/products/${enc(productId)}/goals/new`, coldEmail: !!p?.coldEmail }}
        say={say}
      />
      {toast}
    </div>
  );
}
